/**
 * Nuri Offline Mode — IndexedDB sync queue + offline state management.
 * Uses native IndexedDB (no Dexie dependency to keep bundle small).
 */

const DB_NAME = 'nuri-offline';
const DB_VERSION = 1;
const STORE_NAME = 'sync_queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Queue an API call for later sync (when offline).
 */
export async function queueOfflineAction(action) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      ...action,
      timestamp: new Date().toISOString(),
      synced: false,
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all pending (unsynced) actions.
 */
export async function getPendingActions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result.filter(a => !a.synced));
    req.onerror = () => reject(req.error);
  });
}

/**
 * Mark an action as synced.
 */
export async function markSynced(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(id);
    req.onsuccess = () => {
      const item = req.result;
      if (item) {
        item.synced = true;
        store.put(item);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Clear all synced actions.
 */
export async function clearSynced() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => {
      req.result.filter(a => a.synced).forEach(a => store.delete(a.id));
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Process the sync queue — replay pending actions against the API.
 */
export async function processSyncQueue(authHeaders) {
  const pending = await getPendingActions();
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0, failed = 0;

  for (const action of pending) {
    try {
      const opts = {
        method: action.method || 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
      };
      if (action.body) opts.body = JSON.stringify(action.body);

      const res = await fetch(action.url, opts);
      if (res.ok) {
        await markSynced(action.id);
        synced++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  await clearSynced();
  return { synced, failed, total: pending.length };
}

/**
 * Register the service worker.
 */
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/nuri-sw.js')
      .then(reg => {
        // Listen for sync requests from SW
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data?.type === 'SYNC_REQUESTED') {
            // Trigger sync from the app
            window.dispatchEvent(new CustomEvent('nuri-sync'));
          }
        });
      })
      .catch(() => {});
  }
}

/**
 * Check online status.
 */
export function isOnline() {
  return navigator.onLine;
}
